import axios from "axios";
import { getAuth, onAuthStateChanged, type User } from "firebase/auth";

import {
  completeScheduledQuestionnaire,
  submitAnswers,
} from "@/api/hooks/useSubmissions";
import {
  acknowledgeRequestCommandControllerMarkRead,
  acknowledgeRequestCommandControllerSign,
} from "@/generated/acknowledgements/acknowledgements";
import { completeTaskInstanceCommandControllerComplete } from "@/generated/compliance-tasks/compliance-tasks";
import { createEmergencyLightCheckCommandControllerCreate } from "@/generated/emergency-light-checks/emergency-light-checks";
import { createEventRecordCommandControllerCreate } from "@/generated/event-records/event-records";
import { generateUploadUrlControllerGenerateUploadUrl } from "@/generated/files/files";
import { updateActionStatusCommandControllerUpdateStatus } from "@/generated/follow-up-actions/follow-up-actions";
import { acknowledgeHotHoldingAlertCommandControllerAcknowledge } from "@/generated/hot-holding-alerts/hot-holding-alerts";
import {
  addHotHoldingReadingCommandControllerAddReading,
  logHotHoldingDeliveryCommandControllerLogDelivery,
} from "@/generated/hot-holding-meals/hot-holding-meals";
import { createLogEntryCommandControllerCreate } from "@/generated/log-types/log-types";
import {
  AddHotHoldingReadingDtoReadingType,
  GenerateUploadUrlDtoAssociatedEntityType,
  type IncidentDetailResponseDto,
  LogHotHoldingDeliveryDtoReadingType,
  UpdateActionStatusDtoStatus,
} from "@/generated/model";
import {
  getIncidentQueryControllerGet,
  signOffIncidentCommandControllerSignOff,
} from "@/generated/out-of-range-incidents/out-of-range-incidents";
import {
  trackOfflineModeEntered,
  trackSyncCompleted,
  trackSyncFailed,
} from "@/lib/analytics";

import { offlineDb, type SyncQueueItem } from "./db";

export const MAX_RETRIES = 5;

/**
 * How many times an item may be parked before it is treated as permanently
 * stuck. Parking waits for a *different session* to be able to land the item
 * (NAU-399), but some refusals no session can fix — a record deleted
 * server-side 404s forever. Without a ceiling such an item loops
 * park → sign-in → refuse → park indefinitely: invisible to `errorCount`,
 * untouched by "Retry failed", never reaped because it never syncs, and
 * blocking device reset, which gates on the queue being empty.
 */
export const MAX_PARK_CYCLES = 3;

/**
 * True when no session can land this item: it has been sent and refused until
 * its retries ran out.
 *
 * Spent park cycles deliberately do NOT count. Parking is what an item does
 * while it waits for an entitled colleague, and MAX_PARK_CYCLES bounds only
 * that waiting — once spent, the item stops parking and starts spending retry
 * attempts instead, so it converges here on its own. Treating a park-exhausted
 * item as terminal meant three wrong-person sign-ins, which is an ordinary
 * morning on a shared tablet, were enough to have a record described to a
 * manager as rejected by the server and offered for deletion, when the right
 * colleague signing in would still have sent it.
 */
export function isPermanentlyFailed(item: { attempts: number }): boolean {
  return item.attempts >= MAX_RETRIES;
}

export function createStalledCounts(): StalledCounts {
  return { rejected: 0, parked: 0 };
}

/**
 * Which stalled bucket a queue item belongs in, or null if it is simply
 * waiting its turn.
 *
 * Split out from `countStalled` so the row-shape decision is testable without
 * IndexedDB. Test the buckets through this, not by hand-writing counts —
 * otherwise which bucket a real row lands in has no coverage at all, which is
 * the one step that decides what a manager is told before deleting records.
 */
export function stalledBucketFor(
  item: { attempts: number; deferredUntilAuthChange?: boolean },
  { canSignInDrain }: { canSignInDrain: boolean },
): keyof StalledCounts | null {
  if (isPermanentlyFailed(item)) return "rejected";
  if (!canSignInDrain && item.deferredUntilAuthChange === true) return "parked";
  return null;
}

/**
 * Statuses meaning "THIS session cannot land it", as opposed to "this payload
 * is bad". A manager's queued sign-off drained by a practitioner is refused
 * 403 by the delegation gate; a manager-scoped record the caller cannot read
 * is 404. Both succeed once the right person signs in, so they park instead of
 * consuming a retry — and crucially must NOT be swallowed as terminal by a
 * handler, or the queue item is deleted and the work is destroyed.
 */
function isSessionFixable(status?: number): boolean {
  return status === 401 || status === 403 || status === 404;
}

// A timeout or rate limit (e.g. Cloud Run with no free instance) clears up on
// its own, so the item stays queued.
const RETRYABLE_CLIENT_ERRORS = new Set([408, 425, 429]);

/** A 4xx no other session could fix: the same payload will be refused again. */
function isPayloadRejection(status?: number): boolean {
  if (!status) return false;
  if (isSessionFixable(status)) return false;
  if (RETRYABLE_CLIENT_ERRORS.has(status)) return false;
  return status >= 400 && status < 500;
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** The server's reason for refusing a replay, or undefined when it never answered. */
function rejectionReason(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  if (!error.response) return undefined;
  const message = (error.response.data as { message?: unknown })?.message;
  if (typeof message === "string") return message;
  // ValidationPipe 400s carry one message per failed constraint.
  if (Array.isArray(message) && message.every((m) => typeof m === "string")) {
    return message.join("; ");
  }
  return `Server rejected with ${error.response.status}`;
}

const GCS_UPLOAD_TIMEOUT = 60000; // 60 seconds timeout for GCS uploads
const SYNC_RETRY_DELAY = 300; // 0.3 seconds between retries
const AUTH_READY_TIMEOUT = 10000; // 10 seconds to wait for auth
const DEBOUNCE_DELAY = 100; // 100ms debounce for sync triggers

// Fetch with timeout helper
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Check if network is actually working (not just navigator.onLine)
async function isNetworkReady(): Promise<boolean> {
  if (!navigator.onLine) return false;

  // On mobile, navigator.onLine can be true even when network isn't fully ready
  // We trust it but add a small delay for network stabilization
  return true;
}

class SyncService {
  private isSyncing = false;
  private listeners: Set<(status: SyncStatus) => void> = new Set();
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private authReadyPromise: Promise<boolean> | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  // The uid the current sync run is executing as, so the park branch can record
  // which session failed to land an item.
  private currentAuthUid: string | null = null;
  // Serialised chain of sync runs. Each call to `sync()` appends a new run that
  // starts only after the previous one finishes — this guarantees that a caller
  // who enqueued a queue item *just before* calling sync sees their item picked
  // up, even if a previous sync was already mid-flight. (Critical for
  // submit-then-await flows that need to know the API has been called before
  // they invalidate query caches or navigate.)
  private syncChain: Promise<void> = Promise.resolve();

  // Debounced sync - waits for activity to settle before syncing
  // This prevents hammering the server when user rapidly answers questions
  debouncedSync(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.sync();
    }, DEBOUNCE_DELAY);
  }

  // Immediate sync (cancels any pending debounced sync). Returns the in-flight
  // sync promise so callers in submit flows can serialise UI side-effects
  // (cache invalidation, navigation) on the underlying API call completing —
  // otherwise an immediate refetch races with the queued POST and reads stale
  // server state.
  immediateSync(): Promise<void> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    return this.sync();
  }

  // Wait for Firebase auth to be ready with timeout
  private waitForAuth(timeoutMs: number): Promise<boolean> {
    // If we already have a user, resolve immediately
    const auth = getAuth();
    if (auth.currentUser) {
      return Promise.resolve(true);
    }

    // Reuse existing promise if already waiting
    if (this.authReadyPromise) {
      return this.authReadyPromise;
    }

    this.authReadyPromise = new Promise<boolean>((resolve) => {
      let resolved = false;

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          this.authReadyPromise = null;
          resolve(false);
        }
      }, timeoutMs);

      const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          unsubscribe();
          this.authReadyPromise = null;
          resolve(!!user);
        }
      });
    });

    return this.authReadyPromise;
  }

  subscribe(listener: (status: SyncStatus) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(status: SyncStatus) {
    this.listeners.forEach((listener) => listener(status));
  }

  async getStatus(): Promise<SyncStatus> {
    const pendingCount = await offlineDb.syncQueue.count();
    // "Needs attention" is retry-exhausted OR parked out of cycles — a parked
    // item never increments attempts, so counting attempts alone hid them.
    const errorItems = await offlineDb.syncQueue
      .filter(isPermanentlyFailed)
      .count();

    return {
      pendingCount,
      errorCount: errorItems,
      isSyncing: this.isSyncing,
      isOnline: navigator.onLine,
    };
  }

  // Get sync status for specific scheduled questionnaire IDs
  async getSyncStatusForItems(
    instanceIds: string[],
  ): Promise<Map<string, { pending: number; failed: number }>> {
    const result = new Map<string, { pending: number; failed: number }>();

    if (instanceIds.length === 0) return result;

    // Get all completions for these instance IDs
    const completions = await offlineDb.completions
      .filter((c) => instanceIds.includes(c.instanceId))
      .toArray();

    const completionIdToInstanceId = new Map<string, string>();
    for (const c of completions) {
      completionIdToInstanceId.set(c.id, c.instanceId);
    }

    // Get all sync queue items
    const queueItems = await offlineDb.syncQueue.toArray();

    // Map queue items to their instance IDs
    for (const item of queueItems) {
      let instanceId: string | undefined;

      if (item.type === "completion" || item.type === "finalize") {
        instanceId = completionIdToInstanceId.get(item.entityId);
      } else if (item.type === "answer") {
        const answer = await offlineDb.answers.get(item.entityId);
        if (answer) {
          instanceId = completionIdToInstanceId.get(answer.completionId);
        }
      } else if (item.type === "photo") {
        const photo = await offlineDb.photos.get(item.entityId);
        if (photo) {
          const answer = await offlineDb.answers.get(photo.answerId);
          if (answer) {
            instanceId = completionIdToInstanceId.get(answer.completionId);
          }
        }
      }

      if (instanceId) {
        const current = result.get(instanceId) || { pending: 0, failed: 0 };
        if (isPermanentlyFailed(item)) {
          current.failed++;
        } else {
          current.pending++;
        }
        result.set(instanceId, current);
      }
    }

    return result;
  }

  async sync(): Promise<void> {
    // Chain sync runs so that a call which arrives mid-flight waits for the
    // current run to finish, then performs its own pass over the queue. This
    // ensures items enqueued just before this call are picked up, instead of
    // being silently dropped by the previous run's snapshot of the queue.
    const next = this.syncChain
      .catch(() => undefined)
      .then(() => this._doSync());
    this.syncChain = next;
    return next;
  }

  private async _doSync(): Promise<void> {
    if (this.isSyncing) return;

    // Check network availability
    if (!(await isNetworkReady())) {
      console.log("[Sync] Network not ready, skipping sync");
      return;
    }

    // Wait for Firebase auth to be ready with timeout
    const auth = getAuth();
    if (!auth.currentUser) {
      console.log("[Sync] Waiting for auth to be ready...");
      const authReady = await this.waitForAuth(AUTH_READY_TIMEOUT);
      if (!authReady) {
        console.log("[Sync] Auth not ready after timeout, will retry later");
        return;
      }
    }

    // Force token refresh to ensure we have a valid token after coming back online
    try {
      await auth.currentUser!.getIdToken(true);
    } catch (error) {
      console.error("[Sync] Failed to refresh token:", error);
      return;
    }

    // A *different* user signing in is the event parked items wait for. This
    // compares against the uid stored on each item rather than one held in
    // memory: that field started null on every page load, so a plain refresh
    // un-parked everything even with the same person still signed in, burning a
    // park cycle against a session that provably cannot land the item. Three
    // refreshes exhausted the cycles, the item fell through to normal retries,
    // spent those too, and was then filtered out for good — so the manager who
    // could have landed it never got the chance.
    const authUid = auth.currentUser!.uid;
    this.currentAuthUid = authUid;
    await offlineDb.syncQueue
      .filter(
        (item) =>
          item.deferredUntilAuthChange === true &&
          item.parkedForUid !== authUid &&
          // Only retries being spent stops an item coming back. Suppressing on
          // park cycles too was stricter than intended: three wrong-person
          // sign-ins is an ordinary morning on a shared tablet, and it left the
          // item unreachable — never picked up, never retried, and deleted by
          // the next device reset. Park cycles bound *parking*, so once they
          // are spent the item stops parking and converges through its retry
          // attempts instead.
          item.attempts < MAX_RETRIES,
      )
      .modify({ deferredUntilAuthChange: false });

    this.isSyncing = true;
    this.notify(await this.getStatus());

    const syncStartTime = Date.now();
    let syncedCount = 0;
    let hadErrors = false;

    try {
      // Process queue in priority order
      const items = await offlineDb.syncQueue
        .orderBy("priority")
        .filter(
          (item) =>
            item.attempts < MAX_RETRIES && !item.deferredUntilAuthChange,
        )
        .toArray();

      for (const item of items) {
        try {
          await this.processQueueItem(item);
          // Remove from queue on success
          await offlineDb.syncQueue.delete(item.id);
          syncedCount++;
        } catch (error) {
          console.error(
            `[Sync] Failed for ${item.type} (${item.entityId}):`,
            error,
          );
          hadErrors = true;

          // 401/403/404 mean THIS session cannot land the item, not that the
          // item is bad — a manager's sign-off drained by a practitioner, an
          // expired delegation, a record the caller cannot see. Park it for
          // the next sign-in instead of burning one of its five attempts on
          // every 15s tick (NAU-399). Payload rejections (400) still burn:
          // no session change fixes an invalid claim.
          const status = (error as { response?: { status?: number } })?.response
            ?.status;
          if (
            isSessionFixable(status) &&
            (item.parkCycles ?? 0) < MAX_PARK_CYCLES
          ) {
            await offlineDb.syncQueue.update(item.id, {
              deferredUntilAuthChange: true,
              parkCycles: (item.parkCycles ?? 0) + 1,
              parkedForUid: this.currentAuthUid ?? undefined,
              lastAttempt: Date.now(),
              lastError: `HTTP ${status} — waiting for an authorised user to sign in`,
            });
            this.notify(await this.getStatus());
            continue;
          }

          // Never reached the server — connection refused, DNS, WiFi dropped
          // mid-request. That is not this record's fault, so it must not cost
          // it an attempt. Every capture triggers a sync run, so without this a
          // practitioner recording five checks during a brief outage burns all
          // five attempts on all five records in seconds, and they are then
          // permanently "given up": filtered out of every future sync, with no
          // way back in production, where this debug screen does not exist.
          //
          // `axios.isAxiosError` with no response is the precise test. A plain
          // Error thrown by a handler ("Completion not found") has no response
          // either, but IS the record's own fault and must still converge.
          if (axios.isAxiosError(error) && !error.response) {
            await offlineDb.syncQueue.update(item.id, {
              lastAttempt: Date.now(),
              lastError: "Could not reach the server — will keep trying",
            });
            this.notify(await this.getStatus());
            continue;
          }

          // Track sync failure for items that exceed retry limit
          if (item.attempts + 1 >= MAX_RETRIES) {
            trackSyncFailed({
              errorType: item.type,
              retryCount: item.attempts + 1,
            });
            if (item.type === "acknowledgementSign") {
              await this.restoreFailedAcknowledgementRow(item.entityId);
            }
          }

          // Update retry count
          await offlineDb.syncQueue.update(item.id, {
            attempts: item.attempts + 1,
            lastAttempt: Date.now(),
            lastError: error instanceof Error ? error.message : String(error),
          });

          // Add delay before next item to avoid rapid-fire retries on network issues
          if (item.attempts > 0) {
            await new Promise((resolve) =>
              setTimeout(
                resolve,
                SYNC_RETRY_DELAY * Math.min(item.attempts, 3),
              ),
            );
          }
        }

        this.notify(await this.getStatus());
      }

      // Track sync completion if we processed any items
      if (syncedCount > 0 || items.length > 0) {
        const syncDurationSeconds = Math.floor(
          (Date.now() - syncStartTime) / 1000,
        );
        trackSyncCompleted({
          syncedEventCount: syncedCount,
          syncDurationSeconds,
          hadErrors,
        });
      }
    } finally {
      this.isSyncing = false;
      this.notify(await this.getStatus());
    }
  }

  private async processQueueItem(item: SyncQueueItem): Promise<void> {
    switch (item.type) {
      case "completion":
        // For scheduled questionnaires, completion is just a marker in IndexedDB
        // The actual scheduled questionnaire already exists on the server
        await this.markCompletionSynced(item.entityId);
        break;
      case "answer":
        await this.syncAnswer(item.entityId);
        // Minimal delay after answer sync to ensure IndexedDB commits serverId
        // Photo sync handles missing serverId gracefully with retry
        await new Promise((resolve) => setTimeout(resolve, 50));
        break;
      case "photo": {
        // Check if there are still pending answer syncs - if so, defer photo sync
        const pendingAnswers = await offlineDb.syncQueue
          .filter((i) => i.type === "answer" && i.attempts < MAX_RETRIES)
          .count();
        if (pendingAnswers > 0) {
          throw new Error("Waiting for answer syncs to complete first");
        }
        await this.syncPhoto(item.entityId);
        break;
      }
      case "finalize":
        await this.syncFinalize(item.entityId);
        break;
      case "followUpPhoto": {
        // Check if there are still pending followUpPhoto syncs ahead of this one
        const pendingFollowUpPhotos = await offlineDb.syncQueue
          .filter(
            (i) =>
              i.type === "followUpPhoto" &&
              i.attempts < MAX_RETRIES &&
              i.id !== item.id &&
              i.createdAt < item.createdAt,
          )
          .count();
        if (pendingFollowUpPhotos > 0) {
          // Earlier photos still pending - process in order
        }
        await this.syncFollowUpPhoto(item.entityId);
        break;
      }
      case "followUpFinalize": {
        // Check no pending followUpPhoto items remain for this action
        const pendingPhotos = await offlineDb.syncQueue
          .filter((i) => i.type === "followUpPhoto" && i.attempts < MAX_RETRIES)
          .count();
        if (pendingPhotos > 0) {
          throw new Error(
            "Waiting for follow-up photo uploads to complete first",
          );
        }
        await this.syncFollowUpFinalize(item.entityId);
        break;
      }
      case "logEntry":
        await this.syncLogEntry(item.entityId);
        break;
      case "eventRecord":
        await this.syncEventRecord(item.entityId);
        break;
      case "hotHoldingMeal":
        await this.syncHotHoldingMeal(item.entityId);
        break;
      case "hotHoldingReading":
        await this.syncHotHoldingReading(item.entityId);
        break;
      case "incidentSignoff":
        await this.syncIncidentSignoff(item.entityId);
        break;
      case "hotHoldingAlertAck":
        await this.syncHotHoldingAlertAck(item.entityId);
        break;
      case "acknowledgementSign":
        await this.syncAcknowledgementSign(item.entityId);
        break;
      case "acknowledgementRead":
        await this.syncAcknowledgementRead(item.entityId);
        break;
      case "emergencyLightCheck":
        await this.syncEmergencyLightCheck(item.entityId);
        break;
      case "compliancePhoto":
        await this.syncCompliancePhoto(item.entityId);
        break;
      case "complianceFinalize": {
        // Resolve the instance this finalize targets so we can gate on its
        // own pending photos. Other instances' photos must not block us.
        const completion = await offlineDb.complianceCompletions.get(
          item.entityId,
        );
        if (!completion) {
          throw new Error("Compliance completion not found");
        }
        const pendingPhotosForThisInstance = await offlineDb.syncQueue
          .filter(
            (i) => i.type === "compliancePhoto" && i.attempts < MAX_RETRIES,
          )
          .toArray()
          .then(async (photoQueueItems) => {
            // Look up each photo's instanceId; only count those targeting our instance
            let count = 0;
            for (const queueItem of photoQueueItems) {
              const photo = await offlineDb.compliancePhotos.get(
                queueItem.entityId,
              );
              if (photo && photo.instanceId === item.entityId) count++;
            }
            return count;
          });
        if (pendingPhotosForThisInstance > 0) {
          throw new Error(
            "Waiting for compliance evidence uploads to complete first",
          );
        }
        await this.syncComplianceFinalize(item.entityId);
        break;
      }
    }
  }

  private async syncLogEntry(entryId: string): Promise<void> {
    const entry = await offlineDb.logEntries.get(entryId);
    if (!entry) throw new Error("Log entry not found");
    if (entry.syncState === "synced") return;

    let response: Awaited<
      ReturnType<typeof createLogEntryCommandControllerCreate>
    >;
    try {
      response = await createLogEntryCommandControllerCreate(entry.nurseryId, {
        logTypeId: entry.logTypeId,
        data: entry.data,
        ...(entry.roomId ? { roomId: entry.roomId } : {}),
        // NAU-219d: forward fridge-temp context when present so the server
        // can snapshot thresholds + open an incident at sync time (AC#15).
        ...(entry.coldStorageId ? { coldStorageId: entry.coldStorageId } : {}),
        // NAU-411: name the call point the weekly test covered, so a replayed
        // entry still records which device was checked.
        ...(entry.fireAlarmCallPointId
          ? { fireAlarmCallPointId: entry.fireAlarmCallPointId }
          : {}),
        // NAU-441: name the door an inspection covered; the server requires it.
        ...(entry.fireDoorId ? { fireDoorId: entry.fireDoorId } : {}),
        ...(entry.notes ? { notes: entry.notes } : {}),
        // NAU-399: a queued entry may be replayed long after it was captured,
        // under whoever is signed in by then — on a shared device usually
        // somebody else. Without this the server attributes the check to the
        // sender, crediting the wrong practitioner. The server validates the id
        // against nursery membership and records the real sender separately.
        ...(entry.recordedByUserId
          ? { recordedByUserId: entry.recordedByUserId }
          : {}),
        // The device clock at the moment of the tap. Without it the server
        // stamps arrival time, so a check done at 17:45 and replayed at 17:50
        // reads as 17:50 — falsifying the record and rewriting the gaps that
        // interval-based checks are judged on.
        ...(entry.timestamp ? { capturedAt: entry.timestamp } : {}),
      });
    } catch (error) {
      // Only a refusal the same payload will meet again is kept on the row, so
      // a caller awaiting the sync can show it. No answer, a 5xx or an expired
      // session is left for the queue to retry.
      if (isPayloadRejection(statusOf(error))) {
        await offlineDb.logEntries.update(entryId, {
          lastError: rejectionReason(error),
        });
      }
      throw error;
    }

    const serverId = (response.data as { id: string }).id;

    await offlineDb.logEntries.update(entryId, {
      syncState: "synced",
      serverId,
    });

    console.log(`[Sync] Log entry ${entryId} synced as ${serverId}`);
  }

  /**
   * Sync a queued event record. The leader submitted a structured
   * one-off event (e.g. a fire drill) which may have been captured offline.
   * Event records are immutable, so this is create-only — there is no offline
   * edit or acknowledge path. The server fires the manager notification at
   * create time, which is sync time for an offline submission (per the ticket).
   */
  private async syncEventRecord(recordId: string): Promise<void> {
    const record = await offlineDb.eventRecords.get(recordId);
    if (!record) throw new Error("Event record not found");
    if (record.syncState === "synced") return;

    const response = await createEventRecordCommandControllerCreate(
      record.nurseryId,
      {
        eventTypeId: record.eventTypeId,
        data: record.data,
        // Replayed as captured. The server SKIPS ids that are no longer active
        // members rather than rejecting the record — a drill queued days ago must
        // not be lost because a named colleague has since left (NAU-342).
        acknowledgementRequestUserIds: record.acknowledgementRequestUserIds,
        // NAU-399: replayed under whoever is signed in now, so name the
        // practitioner who actually did it. The server validates the id
        // against nursery membership and records the real sender separately.
        ...(record.recordedByUserId
          ? { recordedByUserId: record.recordedByUserId }
          : {}),
        // See syncLogEntry: the device clock at capture, so a replay does not
        // read as having happened when the tablet reconnected.
        ...(record.timestamp ? { capturedAt: record.timestamp } : {}),
      },
    );

    const serverId = (response.data as { id: string }).id;

    await offlineDb.eventRecords.update(recordId, {
      syncState: "synced",
      serverId,
    });

    console.log(`[Sync] Event record ${recordId} synced as ${serverId}`);
  }

  /**
   * Sync a queued hot holding meal (NAU-93). "Log a delivery" creates the meal
   * plus its first (delivery) reading in one POST; we forward the meal name and
   * that first reading, then back-fill both serverIds. Any queued service
   * readings for this meal unblock once the meal has a serverId.
   */
  private async syncHotHoldingMeal(mealId: string): Promise<void> {
    const meal = await offlineDb.hotHoldingMeals.get(mealId);
    if (!meal) throw new Error("Hot holding meal not found");
    if (meal.syncState === "synced") return;

    const firstReading = await offlineDb.hotHoldingReadings
      .filter((r) => r.mealLocalId === mealId && r.isFirstReading)
      .first();
    if (!firstReading) {
      throw new Error("Hot holding meal has no delivery reading");
    }

    let serverMealId = "";
    let readingId = "";
    try {
      const response = await logHotHoldingDeliveryCommandControllerLogDelivery(
        meal.nurseryId,
        {
          mealName: meal.mealName,
          temperature: firstReading.temperature,
          readingType:
            firstReading.readingType as LogHotHoldingDeliveryDtoReadingType,
          ...(firstReading.notes ? { notes: firstReading.notes } : {}),
          // NAU-399: replayed under whoever is signed in now, so name the
          // practitioner who actually did it. The server validates the id
          // against nursery membership and records the real sender separately.
          ...(meal.capturedByUserId
            ? { recordedByUserId: meal.capturedByUserId }
            : {}),
          // See syncLogEntry: the device clock at capture, so a replay does not
          // read as having happened when the tablet reconnected.
          ...(meal.timestamp ? { capturedAt: meal.timestamp } : {}),
        },
      );
      const data = response.data as { id: string; readingId: string };
      serverMealId = data.id;
      readingId = data.readingId;
    } catch (error) {
      // A 4xx that no other session could fix won't succeed on retry —
      // mark the meal + its delivery reading as errored so the board shows
      // "Sync failed" instead of spinning on "Syncing…" forever. 5xx/network
      // errors rethrow and retry per the normal policy.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (
        status &&
        status >= 400 &&
        status < 500 &&
        !isSessionFixable(status)
      ) {
        await offlineDb.hotHoldingMeals.update(mealId, {
          syncState: "error",
          lastError: `Server rejected with ${status}`,
        });
        await offlineDb.hotHoldingReadings.update(firstReading.id, {
          syncState: "error",
        });
        return;
      }
      throw error;
    }

    // Fail loud rather than back-fill undefined serverIds (which would orphan
    // the meal's readings from their server parent).
    if (!serverMealId || !readingId) {
      throw new Error("logDelivery returned an incomplete response");
    }

    await offlineDb.hotHoldingMeals.update(mealId, {
      syncState: "synced",
      serverId: serverMealId,
    });
    await offlineDb.hotHoldingReadings.update(firstReading.id, {
      syncState: "synced",
      serverId: readingId,
      mealServerId: serverMealId,
    });

    console.log(`[Sync] Hot holding meal ${mealId} synced as ${serverMealId}`);
  }

  /**
   * Sync a queued hot holding reading added to an existing meal (e.g. the
   * service reading). Resolves the parent meal's server id — directly when the
   * meal was already synced, otherwise via the local meal row, deferring (throw
   * → re-queue) until the meal syncs first. Same parent→child gate as syncPhoto.
   *
   * Edge: if the parent meal permanently fails to sync (exhausts retries), this
   * reading also exhausts its retries and is left pending — surfaced via the
   * failed-sync count rather than lost. Mirrors the photo→answer dependency.
   */
  private async syncHotHoldingReading(readingId: string): Promise<void> {
    const reading = await offlineDb.hotHoldingReadings.get(readingId);
    if (!reading) throw new Error("Hot holding reading not found");
    if (reading.syncState === "synced") return;
    // The meal's first reading is created by the meal's POST, not its own item.
    if (reading.isFirstReading) return;

    let mealServerId = reading.mealServerId;
    if (!mealServerId && reading.mealLocalId) {
      const meal = await offlineDb.hotHoldingMeals.get(reading.mealLocalId);
      if (!meal?.serverId) {
        throw new Error("Meal not synced yet - will retry after meal syncs");
      }
      mealServerId = meal.serverId;
    }
    if (!mealServerId) {
      throw new Error("Hot holding reading has no parent meal");
    }

    let serverId = "";
    try {
      const response = await addHotHoldingReadingCommandControllerAddReading(
        mealServerId,
        {
          readingType:
            reading.readingType as AddHotHoldingReadingDtoReadingType,
          temperature: reading.temperature,
          ...(reading.notes ? { notes: reading.notes } : {}),
          // NAU-399: replayed under whoever is signed in now, so name the
          // practitioner who actually did it. The server validates the id
          // against nursery membership and records the real sender separately.
          ...(reading.capturedByUserId
            ? { recordedByUserId: reading.capturedByUserId }
            : {}),
          // See syncLogEntry: the device clock at capture, so a replay does not
          // read as having happened when the tablet reconnected.
          ...(reading.timestamp ? { capturedAt: reading.timestamp } : {}),
        },
      );
      serverId = (response.data as { id: string }).id;
    } catch (error) {
      // 4xx → permanent failure: surface "Sync failed" rather than retry forever.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (
        status &&
        status >= 400 &&
        status < 500 &&
        !isSessionFixable(status)
      ) {
        await offlineDb.hotHoldingReadings.update(readingId, {
          syncState: "error",
          lastError: `Server rejected with ${status}`,
        });
        return;
      }
      throw error;
    }

    if (!serverId) {
      throw new Error("addReading returned an incomplete response");
    }

    await offlineDb.hotHoldingReadings.update(readingId, {
      syncState: "synced",
      serverId,
      mealServerId,
    });

    console.log(
      `[Sync] Hot holding reading ${readingId} synced as ${serverId}`,
    );
  }

  /**
   * Sync a queued incident sign-off (NAU-219d). The manager submitted the
   * dialog while offline (or before any debounced sync ran); we POST to the
   * sign-off endpoint and tidy up local state.
   *
   * Conflict policy is "last writer wins, server authoritative" — if the
   * server returns 409 because another manager signed off first, we mark the
   * local row synced anyway and let the next dashboard refresh reflect the
   * true resolution. A 409 can also mean the sign-off was
   * REJECTED (the latest reading is still out of range), so a 409 is
   * disambiguated by re-reading the incident: still open means rejected, and
   * the optimistic local state must be rolled back rather than marked synced.
   * Other 4xx/5xx surface as errors and re-queue per the normal retry policy.
   */
  private async syncIncidentSignoff(incidentId: string): Promise<void> {
    const signoff = await offlineDb.incidentSignoffs.get(incidentId);
    if (!signoff) throw new Error("Incident sign-off not found");
    if (signoff.syncState === "synced") return;

    try {
      await signOffIncidentCommandControllerSignOff(incidentId, {
        ...(signoff.resolutionNotes
          ? { resolutionNotes: signoff.resolutionNotes }
          : {}),
        // NAU-399: replayed under whoever is signed in now, so name the
        // manager who actually signed off.
        ...(signoff.capturedByUserId
          ? { resolvedByUserId: signoff.capturedByUserId }
          : {}),
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) throw err;
      // A failed confirm read throws out of here and the queue item retries —
      // the next attempt re-409s and re-confirms.
      const incident = (await getIncidentQueryControllerGet(incidentId))?.data;
      if (incident && incident.resolvedAt === null) {
        await this.rollBackRejectedSignoff(incidentId, incident);
        return;
      }
      console.warn(
        `[Sync] Incident ${incidentId} sign-off returned 409 — already resolved server-side`,
      );
    }

    await offlineDb.incidentSignoffs.update(incidentId, {
      syncState: "synced",
    });
    // Drop the cached open-incident row — the dashboard refetch will confirm.
    await offlineDb.openIncidents.delete(incidentId);

    console.log(`[Sync] Incident ${incidentId} signed off`);
  }

  /**
   * The server refused the sign-off (sign-off lock) — the incident is still
   * open. Mark the queued row as an error and restore the open-incident cache
   * row that the dialog optimistically dropped, so the dashboard rail and
   * fridge panel show the incident again and the manager can re-attempt after
   * an in-range reading is recorded.
   */
  private async rollBackRejectedSignoff(
    incidentId: string,
    incident: IncidentDetailResponseDto,
  ): Promise<void> {
    await offlineDb.incidentSignoffs.update(incidentId, {
      syncState: "error",
      lastError:
        "Sign-off rejected — the latest reading for this fridge is still out of range",
    });
    await offlineDb.openIncidents.put({
      id: incident.id,
      nurseryId: incident.nurseryId,
      coldStorageId: incident.coldStorageId,
      coldStorageName: incident.coldStorageName,
      openedAt: incident.openedAt,
      firstReading: {
        id: incident.firstReading.id,
        temperature: incident.firstReading.temperature,
        thresholdMinAtLog: incident.firstReading.thresholdMinAtLog ?? null,
        thresholdMaxAtLog: incident.firstReading.thresholdMaxAtLog ?? null,
        recordedAt: incident.firstReading.recordedAt,
        notes: incident.firstReading.notes ?? null,
      },
      canSignOff: incident.canSignOff,
      cachedAt: Date.now(),
    });
    console.warn(
      `[Sync] Incident ${incidentId} sign-off rejected — latest reading still out of range`,
    );
  }

  /**
   * Replay a queued hot-holding alert acknowledgement (NAU-220). Same
   * "last writer wins, server authoritative" policy as incident sign-off: a
   * 409 (already acknowledged by another manager) is treated as success.
   *
   * Defensive since NAU-391 made the endpoint idempotent: only a queued ack
   * landing on an older API still 409s. Dropping the branch would make that
   * throw and retry forever, unseen — the row is already gone locally.
   */
  private async syncHotHoldingAlertAck(alertId: string): Promise<void> {
    const ack = await offlineDb.hotHoldingAlertAcks.get(alertId);
    if (!ack) throw new Error("Hot holding alert ack not found");
    if (ack.syncState === "synced") return;

    try {
      await acknowledgeHotHoldingAlertCommandControllerAcknowledge(alertId, {
        // NAU-399: replayed under whoever is signed in now, so name the
        // manager who actually acknowledged it.
        ...(ack.capturedByUserId
          ? { acknowledgedByUserId: ack.capturedByUserId }
          : {}),
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) throw err;
      console.warn(
        `[Sync] Hot holding alert ${alertId} acknowledge returned 409 — already acknowledged server-side`,
      );
    }

    await offlineDb.hotHoldingAlertAcks.update(alertId, {
      syncState: "synced",
    });
    // Drop the cached open-alert row — the dashboard refetch will confirm.
    await offlineDb.openHotHoldingAlerts.delete(alertId);

    console.log(`[Sync] Hot holding alert ${alertId} acknowledged`);
  }

  /**
   * Replays a staff member's signature (NAU-410 AC35).
   *
   * `signedAt` is sent from the stored row rather than left to the server, so
   * AC16's "date acknowledged" is the moment they actually confirmed — not the
   * moment the queue happened to drain, which could be days later.
   *
   * A 409 is treated as success: it means the request was superseded or already
   * signed, and either way there is nothing more this replay can usefully do.
   * Surfacing it as a failure would show the staff member a red banner for
   * something they completed.
   */
  private async syncAcknowledgementSign(requestId: string): Promise<void> {
    const ack = await offlineDb.acknowledgementAcks.get(requestId);
    if (!ack) throw new Error("Acknowledgement ack not found");
    if (ack.signSyncState === "synced") return;

    try {
      await acknowledgeRequestCommandControllerSign(requestId, {
        signedAt: ack.signedAt,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) {
        // Record the failure on the ack row before rethrowing. The queue's own
        // retry bookkeeping lives on the queue item, which nothing renders — so
        // without this a signature that exhausts all five retries just vanishes
        // and the "Sync failed" badge on the To do row can never appear.
        await offlineDb.acknowledgementAcks.update(requestId, {
          signSyncState: "error",
          lastError: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
      console.warn(
        `[Sync] Acknowledgement ${requestId} sign returned 409 — already signed or superseded server-side`,
      );
    }

    await offlineDb.acknowledgementAcks.update(requestId, {
      signSyncState: "synced",
      lastError: undefined,
    });
    console.log(`[Sync] Acknowledgement ${requestId} signed`);
  }

  /**
   * Puts a signature's To do row back after it has exhausted every retry.
   *
   * Signing removes the row optimistically so it disappears immediately. That
   * is right while the sync is still in flight, but once it has permanently
   * failed the staff member would otherwise be left believing they signed
   * something that never saved. Restoring the row lets the "Sync failed" badge
   * render and gives them something to retry.
   */
  private async restoreFailedAcknowledgementRow(
    requestId: string,
  ): Promise<void> {
    const ack = await offlineDb.acknowledgementAcks.get(requestId);
    if (!ack?.removedListEntry || !ack.removedListKey) return;

    const cached = await offlineDb.acknowledgementRequests.get(
      ack.removedListKey,
    );
    if (!cached) return;

    const requests = (cached.requests as { requestId: string }[]) ?? [];
    if (requests.some((request) => request.requestId === requestId)) return;

    await offlineDb.acknowledgementRequests.put({
      ...cached,
      requests: [...requests, ack.removedListEntry],
    });
    console.warn(
      `[Sync] Acknowledgement ${requestId} exhausted its retries — restored to the To do list`,
    );
  }

  /**
   * Replays "they opened the document".
   *
   * Deliberately the most forgiving worker in the queue. Read is telemetry-grade
   * — losing it costs a manager one column on a roster, whereas losing a
   * signature costs a compliance record — so a 409 (superseded) is success, and
   * this runs at the lowest priority so it can never delay a signature or burn
   * the retry budget on something nobody is waiting for.
   */
  private async syncAcknowledgementRead(requestId: string): Promise<void> {
    const ack = await offlineDb.acknowledgementAcks.get(requestId);
    if (!ack) throw new Error("Acknowledgement ack not found");
    if (ack.readSyncState === "synced") return;

    try {
      await acknowledgeRequestCommandControllerMarkRead(requestId, {
        readAt: ack.readAt,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 409) {
        await offlineDb.acknowledgementAcks.update(requestId, {
          readSyncState: "error",
          lastError: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
      console.warn(
        `[Sync] Acknowledgement ${requestId} read returned 409 — superseded server-side`,
      );
    }

    await offlineDb.acknowledgementAcks.update(requestId, {
      readSyncState: "synced",
    });
  }

  /** Replays a whole emergency lighting check in one request: AC13 completeness is all-or-nothing. */
  private async syncEmergencyLightCheck(localId: string): Promise<void> {
    const check = await offlineDb.emergencyLightChecks.get(localId);
    if (!check) throw new Error("Emergency light check not found");
    if (check.syncState === "synced") return;

    let response: Awaited<
      ReturnType<typeof createEmergencyLightCheckCommandControllerCreate>
    >;
    try {
      response = await createEmergencyLightCheckCommandControllerCreate(
        check.nurseryId,
        {
          kind: check.kind,
          items: check.items,
          // When the inspection was done, not when it synced: the server dates
          // the record by it and judges AC13 against the lights in service then.
          capturedAt: check.queuedAt,
          // NAU-399: name the practitioner who recorded it, so the server does not
          // credit whoever is signed in when the queue drains.
          ...(check.capturedByUserId
            ? { recordedByUserId: check.capturedByUserId }
            : {}),
        },
      );
    } catch (error) {
      // Same rule as syncHotHoldingMeal: retrying won't help, so mark it failed
      // with the server's reason ("Missing: Light 3") and let the queue drop it.
      // Anything else — no answer, a 5xx, an expired session — stays queued,
      // with no reason stored, so the form doesn't discard a retryable check.
      if (isPayloadRejection(statusOf(error))) {
        await offlineDb.emergencyLightChecks.update(localId, {
          syncState: "error",
          lastError: rejectionReason(error),
        });
        return;
      }
      throw error;
    }

    await offlineDb.emergencyLightChecks.update(localId, {
      syncState: "synced",
      // Written back so the caller can deep-link into the follow-up flow for a
      // failed light; the item ids only exist once the server has the check.
      serverId: response.data.id,
      serverItems: response.data.items,
    });

    console.log(`[Sync] Emergency light check ${localId} submitted`);
  }

  private async markCompletionSynced(completionId: string): Promise<void> {
    const completion = await offlineDb.completions.get(completionId);
    if (!completion) throw new Error("Completion not found");
    if (completion.syncState === "synced") return;

    // For scheduled questionnaires, the instanceId IS the scheduledQuestionnaireId
    // No need to create anything on the server - just mark as synced
    await offlineDb.completions.update(completionId, {
      serverId: completion.instanceId, // Use instanceId as serverId
      syncState: "synced",
    });
  }

  private async syncAnswer(answerId: string): Promise<void> {
    const answer = await offlineDb.answers.get(answerId);
    if (!answer) throw new Error("Answer not found");
    if (answer.syncState === "synced") return;

    // Get the completion to find the scheduledQuestionnaireId
    const completion = await offlineDb.completions.get(answer.completionId);
    if (!completion) {
      throw new Error("Completion not found for answer");
    }

    // Submit the answer to the scheduled questionnaire
    const response = await submitAnswers({
      scheduledQuestionnaireId: completion.instanceId,
      answers: [
        {
          questionId: answer.itemId,
          answerValue: answer.answer,
          notes: answer.notes,
        },
      ],
      // NAU-399: replayed under whoever is signed in now, so name the
      // practitioner who actually answered.
      ...(completion.practitionerId
        ? { answeredByUserId: completion.practitionerId }
        : {}),
    });

    // Find the answerId from response
    const serverAnswer = response.answers.find(
      (a) => a.questionId === answer.itemId,
    );

    await offlineDb.answers.update(answerId, {
      serverId: serverAnswer?.answerId,
      syncState: "synced",
    });
  }

  private async syncPhoto(photoId: string): Promise<void> {
    const photo = await offlineDb.photos.get(photoId);
    if (!photo) throw new Error("Photo not found");
    if (photo.syncState === "synced") return;

    // Get the answer to find the server-side answerId
    const answer = await offlineDb.answers.get(photo.answerId);
    if (!answer) throw new Error("Answer not found for photo");

    // If answer hasn't been synced yet, we can't upload the photo
    if (!answer.serverId) {
      throw new Error("Answer not synced yet - will retry after answer syncs");
    }

    // If we already have a serverId (fileId), regenerate URL for retry
    // Otherwise, generate new upload URL (creates File record on server)
    // photo.data is already ArrayBuffer (stored that way for iOS/Safari compatibility)
    const arrayBuffer = photo.data;

    const uploadUrlResponse =
      await generateUploadUrlControllerGenerateUploadUrl({
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        size: arrayBuffer.byteLength,
        associatedEntityType: GenerateUploadUrlDtoAssociatedEntityType.ANSWER,
        associatedEntityId: answer.serverId,
        // Pass existing fileId for retries to avoid creating duplicate File records
        fileId: photo.serverId,
      });

    const fileId = uploadUrlResponse.data.fileId;
    const uploadUrl = uploadUrlResponse.data.uploadUrl;

    if (!fileId || !uploadUrl) {
      throw new Error("Invalid response from generate upload URL");
    }

    // Store fileId immediately so retries won't create duplicate File records
    if (!photo.serverId) {
      await offlineDb.photos.update(photoId, { serverId: fileId });
    }

    // Upload to GCS signed URL with timeout
    let uploadResponse: Response;
    try {
      uploadResponse = await fetchWithTimeout(
        uploadUrl,
        {
          method: "PUT",
          headers: {
            "Content-Type": photo.mimeType,
          },
          body: arrayBuffer,
        },
        GCS_UPLOAD_TIMEOUT,
      );
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(
          `Photo upload timed out after ${GCS_UPLOAD_TIMEOUT / 1000}s - will retry`,
        );
      }
      throw error;
    }

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(
        `Failed to upload photo to storage: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`,
      );
    }
    console.log(`[Sync] Photo ${photoId} uploaded successfully`);

    // Delete the photo from IndexedDB after successful upload
    // The server now has the file, so we don't need to keep a local copy
    // This prevents duplicates when the page reloads and server returns the file
    await offlineDb.photos.delete(photoId);
  }

  private async syncFinalize(completionId: string): Promise<void> {
    const completion = await offlineDb.completions.get(completionId);
    if (!completion) {
      throw new Error("Completion not found");
    }

    // Mark the scheduled questionnaire as completed on the server
    // NAU-399: replayed under whoever is signed in now, so name the
    // practitioner who actually completed the checklist.
    await completeScheduledQuestionnaire(
      completion.instanceId,
      completion.practitionerId || undefined,
    );

    // Finalising sets the row back to "pending" before queueing this, so it has
    // to be cleared again here or every completed checklist stays "pending"
    // forever — reporting work as unsent long after the server accepted it.
    // The compliance and follow-up finalize handlers do the same.
    await offlineDb.completions.update(completionId, { syncState: "synced" });
  }

  private async syncFollowUpPhoto(photoId: string): Promise<void> {
    const photo = await offlineDb.followUpPhotos.get(photoId);
    if (!photo) throw new Error("Follow-up photo not found");
    if (photo.syncState === "synced") return;

    const arrayBuffer = photo.data;

    const uploadUrlResponse =
      await generateUploadUrlControllerGenerateUploadUrl({
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        size: arrayBuffer.byteLength,
        associatedEntityType:
          GenerateUploadUrlDtoAssociatedEntityType.FOLLOW_UP_ACTION,
        associatedEntityId: photo.actionId,
        fileId: photo.serverId,
      });

    const fileId = uploadUrlResponse.data.fileId;
    const uploadUrl = uploadUrlResponse.data.uploadUrl;

    if (!fileId || !uploadUrl) {
      throw new Error("Invalid response from generate upload URL");
    }

    // Store fileId immediately so retries won't create duplicate File records
    if (!photo.serverId) {
      await offlineDb.followUpPhotos.update(photoId, { serverId: fileId });
    }

    // Upload to GCS signed URL with timeout
    let uploadResponse: Response;
    try {
      uploadResponse = await fetchWithTimeout(
        uploadUrl,
        {
          method: "PUT",
          headers: { "Content-Type": photo.mimeType },
          body: arrayBuffer,
        },
        GCS_UPLOAD_TIMEOUT,
      );
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(
          `Follow-up photo upload timed out after ${GCS_UPLOAD_TIMEOUT / 1000}s - will retry`,
        );
      }
      throw error;
    }

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(
        `Failed to upload follow-up photo: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`,
      );
    }

    console.log(`[Sync] Follow-up photo ${photoId} uploaded successfully`);

    // Delete from IndexedDB after successful upload
    await offlineDb.followUpPhotos.delete(photoId);
  }

  private async syncCompliancePhoto(photoId: string): Promise<void> {
    const photo = await offlineDb.compliancePhotos.get(photoId);
    if (!photo) throw new Error("Compliance photo not found");
    if (photo.syncState === "synced") return;

    const arrayBuffer = photo.data;

    const uploadUrlResponse =
      await generateUploadUrlControllerGenerateUploadUrl({
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        size: arrayBuffer.byteLength,
        associatedEntityType:
          GenerateUploadUrlDtoAssociatedEntityType.COMPLIANCE_TASK_INSTANCE,
        associatedEntityId: photo.instanceId,
        fileId: photo.serverId,
      });

    const fileId = uploadUrlResponse.data.fileId;
    const uploadUrl = uploadUrlResponse.data.uploadUrl;

    if (!fileId || !uploadUrl) {
      throw new Error("Invalid response from generate upload URL");
    }

    // Persist serverId immediately so retries de-dupe at the File row level.
    if (!photo.serverId) {
      await offlineDb.compliancePhotos.update(photoId, { serverId: fileId });
    }

    let uploadResponse: Response;
    try {
      uploadResponse = await fetchWithTimeout(
        uploadUrl,
        {
          method: "PUT",
          headers: { "Content-Type": photo.mimeType },
          body: arrayBuffer,
        },
        GCS_UPLOAD_TIMEOUT,
      );
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error(
          `Compliance photo upload timed out after ${GCS_UPLOAD_TIMEOUT / 1000}s - will retry`,
        );
      }
      throw error;
    }

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(
        `Failed to upload compliance photo: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`,
      );
    }

    console.log(`[Sync] Compliance photo ${photoId} uploaded successfully`);

    // Mark as synced in IndexedDB rather than delete. The hook re-reads photos
    // from Dexie when sync status changes, so the UI transitions from "Syncing…"
    // to a clean state without the blob being lost from the screen mid-upload.
    // (Synced rows are retained: logout deliberately preserves work tables,
    //  see clearAllOfflineData. Nothing reaps them today.)
    await offlineDb.compliancePhotos.update(photoId, { syncState: "synced" });
  }

  private async syncComplianceFinalize(instanceId: string): Promise<void> {
    const completion = await offlineDb.complianceCompletions.get(instanceId);
    if (!completion) {
      throw new Error("Compliance completion not found");
    }

    // Translate the local Map shape into the DTO array shape the API expects
    const checklistResults = Object.entries(completion.checklistResults).map(
      ([itemDefinitionId, value]) => ({
        itemDefinitionId,
        result: value.result,
        ...(value.notes ? { notes: value.notes } : {}),
      }),
    );

    try {
      await completeTaskInstanceCommandControllerComplete(instanceId, {
        ...(completion.notes.trim() ? { notes: completion.notes.trim() } : {}),
        // NAU-399: replayed under whoever is signed in now, so name the
        // practitioner who actually completed the task.
        ...(completion.capturedByUserId
          ? { completedByUserId: completion.capturedByUserId }
          : {}),
        ...(Object.keys(completion.externalServiceData).length > 0
          ? { externalServiceData: completion.externalServiceData }
          : {}),
        ...(checklistResults.length > 0 ? { checklistResults } : {}),
        ...(completion.nextDueDate
          ? { nextDueDate: completion.nextDueDate }
          : {}),
        ...(completion.submitAsAwaiting === true
          ? { submitAsAwaiting: true }
          : {}),
      });
    } catch (error) {
      // 4xx (e.g. instance already completed by someone else, or a next due
      // date outside the task's current window) → mark as rejected rather than
      // retry forever. Keep the server's reason: the completion screen shows it
      // so the user can correct their answer and resubmit.
      if (isPayloadRejection(statusOf(error))) {
        await offlineDb.complianceCompletions.update(instanceId, {
          syncState: "error",
          conflict: true,
          lastError: rejectionReason(error),
        });
        // Drop the queue item so we don't keep retrying a permanently rejected payload.
        return;
      }
      throw error;
    }

    await offlineDb.complianceCompletions.update(instanceId, {
      syncState: "synced",
    });

    console.log(
      `[Sync] Compliance task instance ${instanceId} finalized successfully`,
    );
  }

  private async syncFollowUpFinalize(actionId: string): Promise<void> {
    const completion = await offlineDb.followUpCompletions.get(actionId);
    if (!completion) {
      throw new Error("Follow-up completion not found");
    }

    // Call the status update API
    await updateActionStatusCommandControllerUpdateStatus(actionId, {
      status: UpdateActionStatusDtoStatus.COMPLETED,
      // NAU-399: replayed under whoever is signed in now, so name the
      // practitioner who actually completed the action.
      ...(completion.capturedByUserId
        ? { performedByUserId: completion.capturedByUserId }
        : {}),
      ...(completion.completionNotes.trim()
        ? { completionNotes: completion.completionNotes.trim() }
        : {}),
    });

    // Mark completion as synced
    await offlineDb.followUpCompletions.update(actionId, {
      syncState: "synced",
    });

    // Update cached follow-up actions list in IndexedDB
    const allCaches = await offlineDb.followUpActions.toArray();
    for (const cache of allCaches) {
      const items = cache.items as Array<{ id: string; status: string }>;
      const updated = items.map((item) =>
        item.id === actionId ? { ...item, status: "COMPLETED" } : item,
      );
      await offlineDb.followUpActions.update(cache.id, { items: updated });
    }

    console.log(`[Sync] Follow-up action ${actionId} finalized successfully`);
  }

  // Start periodic sync check (every 15 seconds) when there are pending items
  startPeriodicSync(): void {
    if (this.syncInterval) return;

    this.syncInterval = setInterval(async () => {
      const status = await this.getStatus();
      if (status.pendingCount > 0 && navigator.onLine) {
        console.log(
          "[Sync] Periodic check - pending items:",
          status.pendingCount,
        );
        this.sync();
      }
    }, 15000); // 15 seconds
  }

  stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Queue items that cannot be drained, split by why.
   *
   * `canSignInDrain` is a required argument rather than an assumption, because
   * the answer genuinely differs by caller. Device reset runs from the pre-auth
   * PIN screen, where `loginForDeviceAuth` verifies a manager over HTTP without
   * signing them into Firebase — no Firebase user means `sync()` returns at its
   * auth gate, so a parked item is stalled there however many cycles it has
   * left. But that is a property of the caller, not of the app: a session
   * rejected by `/v1/users/self` clears its cached session WITHOUT signing out
   * of Firebase, so the same screen can be reached with `auth.currentUser` still
   * set — and then parked items really can drain. Keep this an argument: encode
   * the pre-auth case as an assumption instead and a reset wipes recoverable
   * work in exactly that state.
   */
  async countStalled({
    canSignInDrain,
  }: {
    canSignInDrain: boolean;
  }): Promise<StalledCounts> {
    const items = await offlineDb.syncQueue.toArray();

    return items.reduce<StalledCounts>((counts, item) => {
      const bucket = stalledBucketFor(item, { canSignInDrain });
      if (bucket) counts[bucket] += 1;
      return counts;
    }, createStalledCounts());
  }

  async retryFailed(): Promise<void> {
    // Reset failed items, INCLUDING parked ones. Parked items sit at
    // attempts: 0 by design, so filtering on attempts alone made "Retry
    // failed" a no-op for exactly the items a user would press it for.
    await offlineDb.syncQueue
      .filter(
        (item) =>
          item.attempts >= MAX_RETRIES || item.deferredUntilAuthChange === true,
      )
      .modify({
        attempts: 0,
        parkCycles: 0,
        deferredUntilAuthChange: false,
        parkedForUid: undefined,
        lastError: undefined,
      });

    await this.sync();
  }
}

/** Why a queue item cannot be sent, for callers that must explain it to a user. */
export interface StalledCounts {
  /** The server refused these; no sign-in changes that. */
  rejected: number;
  /** These are waiting for an authorised user, and CAN still be sent. */
  parked: number;
}

export interface SyncStatus {
  pendingCount: number;
  errorCount: number;
  isSyncing: boolean;
  isOnline: boolean;
}

export const syncService = new SyncService();

// Auto-sync when online
const SYNC_START_DELAY = 1000; // 1 second for network events (online, visibility)

if (typeof window !== "undefined") {
  // Sync when network comes back online
  window.addEventListener("online", () => {
    console.log("[Sync] Online event - triggering sync");
    syncService.startPeriodicSync();
    // Delay to let network stabilize on mobile
    setTimeout(() => syncService.sync(), SYNC_START_DELAY);
  });

  // Stop sync when offline
  window.addEventListener("offline", () => {
    console.log("[Sync] Offline event - stopping periodic sync");
    syncService.stopPeriodicSync();
    trackOfflineModeEntered();
  });

  // Sync when app comes back to foreground (important for mobile PWA)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine) {
      console.log("[Sync] App visible - triggering sync");
      // Delay for mobile PWA to fully wake up and stabilize
      setTimeout(() => syncService.sync(), SYNC_START_DELAY);
    }
  });

  // Sync on app load if online
  // Use longer delay on initial load to allow Firebase auth to initialize
  if (navigator.onLine) {
    syncService.startPeriodicSync();
    // Wait for app to fully initialize before first sync
    setTimeout(() => {
      console.log("[Sync] Initial load - triggering sync");
      syncService.sync();
    }, SYNC_RETRY_DELAY);
  }
}
